<template>
	<Teleport to="html">
		<InviteUsersModal :show="showInviteModal" @onDismiss="showInviteModal = false; reloadTable();" />
	</Teleport>

	<AdminLayout>
		<div class="flex flex-row mb-4">
			<p class="admin-center-subtitle">User Invites</p>
			<SubmitButton label="Invite" @click="onInviteClicked" size="small" class="mr-2"/>
		</div>

		<PortalDataTable
			ref="table"
			:endpoints="endpoints"
			:enableRowSelectFn="canSelectRow"
		>
			<template #advanced-search>
				<div class="flex flex-row mr-1 mt-1 justify-end">
					<div class="flex w-full justify-end overflow-hidden gap-4">
						<Popper :content="`Show invites that have been deleted.`" hover arrow placement="bottom-start">
							<CheckBox input-id="cbx_archive" label="Include deleted invites" label-location="left" class="my-2" :checked="false"
										 v-model="includeDeleted"
										 @checked-changed="onAdvancedSearchClicked" />
						</Popper>
						<Popper :content="`Show invites that have been accepted.`" hover arrow placement="bottom-start">
							<CheckBox input-id="cbx_accepted" label="Include accepted invites" label-location="left" class="my-2" :checked="false"
										 v-model="includeAccepted"
										 @checked-changed="onAdvancedSearchClicked" />
						</Popper>
					</div>
				</div>
			</template>

			<template #table-menu>
				<div v-if="canResendInvite" class="h-10 bg-primary flex flex-row justify-between">
					<div class="text-label-on-color text-center flex items-center">
						<div class="ml-2">
							{{ rowsSelected }} Items Selected
						</div>
					</div>
					<div class="flex flex-row">
						<Popper :content="`Only expired invites can be resent.`" hover arrow placement="bottom-start" :disabled="!showResendPopper">
							<SubmitButton :size="ButtonSize.ExtraSmall" label="Resend" :disabled="!getResendEnabled()" @click="onTableMenuItemClicked('resend_selected')"></SubmitButton>
						</Popper>
						<Popper :content="`Only expired invites can be deleted.`" hover arrow placement="bottom-start" :disabled="!showDeletePopper">
							<SubmitButton :size="ButtonSize.ExtraSmall" label="Delete" :disabled="!getDeleteEnabled()" @click="onTableMenuItemClicked('delete_selected')"></SubmitButton>
						</Popper>
						<SubmitButton :size="ButtonSize.ExtraSmall" label="Cancel" :disabled="!cancelEnabled" @click="onTableMenuItemClicked('cancel')"></SubmitButton>
					</div>
				</div>
			</template>

			<template #email="data">
				<div class="userpilot-mask whitespace-nowrap">
					{{ data.value.email }}
				</div>
			</template>

			<template #organization_name="data">
				<p class="endpoint-clickable-text whitespace-pre-wrap xl:whitespace-nowrap w-fit" :class="[{ 'line-through': data.value.organization_deleted_at }]"
				   @click.prevent="router.push({ name: 'admin.organizations', query: { show_org_id: data.value.organization_id } })">
					{{ data.value.organization_name }}<br>{{ (data.value.organization_name_dba ? "(" + data.value.organization_name_dba + ")" : "") }}
				</p>
			</template>

			<template #organization_role_name="data">
				{{ data.value.organization_role_name }}
			</template>

			<template #created_at="data">
				<div class="flex w-full justify-center whitespace-nowrap">
					{{ Format.date(data.value.created_at) }}
				</div>
			</template>

			<template #status="data">
				<div class="flex justify-center items-center min-w-22 py-1 px-3 rounded-md whitespace-nowrap"
				     :class="getStatusStyle(data.value.status)">
					{{ data.value.status }}
				</div>
			</template>

			<template #error_message="data">
				<div class="userpilot-mask">
					{{ getInfoText(data.value) }}
				</div>
			</template>

		</PortalDataTable>
	</AdminLayout>
</template>

<script setup lang="js">
/**
 * @typedef {Object} InviteRecord
 * @property {number} id ID
 * @property {string} email Email invite was sent to
 * @property {number} organization_id Id of organization user will be added to when accepting
 * @property {number} organization_role_id Id of role user will be given when accepting (bulk upload only)
 * @property {number|null} user_id Id created for the user when the invite was accepted
 * @property {string} modifier_name Name of the user that last modified the record
 * @property {string} inviter_name Name of the user that invited the user
 * @property {number} invitee_user_id ID of the user that invited the user
 * @property {number} modified_by_user_id User id of last modifier
 * @property {string|null} created_at Timestamp created
 * @property {string|null} deleted_at Timestamp deleted
 * @property {string} error_message Error message
 * @property {boolean} error_eh True if an error occurred while sending
 * @property {boolean} expired True if this invite has expired
 * @property {Object} organization Object containing organization data related to organization_id
 */


//#region Imports
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import AdminLayout from "@/layouts/AdminLayout.vue";
import { computed, ref } from 'vue'
import InviteUsersModal from '@/views/Admin/User/InviteUsersModal.vue'
import router from '@/router/router.js'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { useAuthStore } from '@/stores/authStore.js'
import { userAdminApi } from '@/api/User/UserAdminApi.js'
import { Toaster } from '@/models/utils/Toaster.js'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'
import CheckBox from '@/components/Forms/CheckBox.vue'
import { ButtonSize } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import { Format } from '@/models/utils/FormatUtils.js'
//#endregion Imports

//#region Setup
const authStore = useAuthStore()
const showInviteModal = ref(false);
const includeDeleted = ref(false)
const includeAccepted = ref(false)
const showResendPopper = ref(false);
const showDeletePopper = ref(false);

const canResendInvite = ref(authStore.isOrganizationAdmin || authStore.isPortalAdmin)

const toaster = new Toaster();
//#endregion Setup

//#region Table Setup
/**
 * Advanced Search Checkboxes
 * @type @type {import('vue').ComputedRef<string>}
 */
const advancedSearchParams = computed(() => {
	return [
		{
			"id": 'deleted_at',
			"value": includeDeleted.value
		},
		{
			"id": 'accepted',
			"value": includeAccepted.value
		},
	]
});

/** @type {import('vue').Ref<Object>} */
const table = ref(null);
const columns = [
	{ field: "id", title: "ID", isUnique: true, hide: true },
	{ field: "email", title: "Email", search: true, width: '15%' },
	{ field: "organization_name", title: "Organization", search: true },
	{ field: "organization_role_name", title: "Role", search: true },
	{ field: "created_at", title: "Date Sent", width: '1%', sort:true },
	{ field: "status", title: "Status", width: '1%', search: true },
	{ field: "error_message", title: "Information", hide:!authStore.isDeveloper, width: '20%', search: true, sort:false },
	{ field: "inviter_name", title: "inviter_name", hide:true, search: true },
	{ field: "modifier_name", title: "modifier_name", hide:true, search: true },
];

/**
 * Defines the table setup
 * @type {DataTableEndpointDefinition[]}
 */
const endpoints = [
	new DataTableEndpointDefinition({
		endpointRoute: 'users/invites',
		endpointDisplayName: 'User Invites',
		advancedSearchParams: advancedSearchParams,
		initialSortColumn: 'created_at',
		searchPlaceholder: 'Search...',
		columns: columns,
		showCheckboxes: true
	})
];

//#endregion Table Setup

//#region Table Event Handlers
/**
 * Called when the Invite button is clicked
 */
function onInviteClicked() {
	showInviteModal.value = true;
}

/**
 * Unselects a row on a slight delay to
 * @param {number} p_row_index Index of the row to unselect
 */
function unselectRow(p_row_index)
{
	//Set timeout needed for it work right
	setTimeout(function(){table.value.unselectRow(p_row_index)}, 10)
}

/**
 * Checks if a selected row can be selected. If not returns false.
 * ID column must be set with isUnique: true for this to work with the select all column.
 * @param {InviteRecord} p_row Row data
 * @returns {boolean} True if row can be selected
 */
function canSelectRow(p_row)
{
	if (p_row['deleted_at']) return false;
	return p_row['user_id'] <= 0;
}

/**
 * Resends one or more invites
 * @param {Array<number>} p_ids Array of invite ids
 */
function resendInvites(p_ids)
{
	userAdminApi.resendInvites(p_ids,
		()=>{
			reloadTable()
			toaster.success("Invite was successfully resent.");
		},
		()=>{
			toaster.success("Invite failed to resend.");
		})
}

/**
 * Deletes one or more invites
 * @param {Array<number>} p_ids Array of invite ids
 */
function deleteInvites(p_ids)
{
	userAdminApi.deleteInvites(p_ids,
		()=>{
			reloadTable()
			toaster.success("Invite was successfully deleted.");
		},
		()=>{
			toaster.success("Failed to delete invite.");
		})
}

/**
 * Reloads the table data when an advanced search checkbox is selected.
 */
function onAdvancedSearchClicked() {
	table.value.loadData()
}

/**
 * Called when a button in the table menu bar is selected
 * @param {string} p_menu_item Text identifying which button was clicked
 */
function onTableMenuItemClicked(p_menu_item)
{
	const sel_rows = table.value.getSelectedRows();
	const ids = sel_rows.map(p_item => p_item.id);
	switch (p_menu_item)
	{
		case "resend_selected":
			resendInvites(ids)
			break;
		case "delete_selected":
			deleteInvites(ids)
			break;
		case "cancel":
			for (let i = 0; i < table.value.getRows().length; i++)
			{
				unselectRow(i)
			}
			break;
		default:
			alert("Not yet implemented")
			break;
	}
}
//#endregion Table Event Handlers

//#region Table Functions

/**
 * Computed value to display the number of records selected in the menu bar.
 * @type @type {import('vue').ComputedRef<number>}
 */
const rowsSelected = computed(() => {
	if (!table.value) return 0
	return table.value.getSelectedRows().length
});

/**
 * Reloads the table
 */
function reloadTable()
{
	if (table.value)
	{
		table.value.loadData()
	}
	else
	{
		location.reload()
	}
}

/**
 * Get the style to show around the status. Status is set in the UserInvite model with statusCaseSql.
 * @param {string} p_status Status value in row data
 * @returns {string}
 */
function getStatusStyle(p_status)
{
	let style = 'bg-neutral-light text-label';

	switch (p_status)
	{
		case 'Pending':
			style = 'bg-neutral-light text-label'
			break;

		case 'Accepted':
			style = 'bg-success text-success-on-color'
			break;

		case 'Deleted':
			style = 'bg-information text-information-on-color'
			break;

		case 'Expired':
			style = 'bg-lo-warning-subtle text-lo-warning-on-color';
			break;

		case 'Error':
			style = 'bg-error-light text-error-on-color';
			break;
	}
	return style;
}

/**
 * Checks if the Resend button should be enabled based on the selected rows.
 * @returns {boolean} True if the button should be enabled
 */
function getResendEnabled()
{
	if (table.value)
	{
		const sel_rows = table.value.getSelectedRows();
		showResendPopper.value = false
		let can_eh = sel_rows.length > 0;
		for (let i = 0; i < sel_rows.length; i++)
		{
			if (sel_rows[i].status !== 'Expired')
			{
				can_eh = false
				showResendPopper.value = true
				break;
			}
		}
		return can_eh
	}
	return false
}

/**
 * Checks if the Delete button should be enabled based on the selected rows.
 * @returns {boolean} True if the button should be enabled
 */
function getDeleteEnabled()
{
	if (table.value)
	{
		const sel_rows = table.value.getSelectedRows();
		showDeletePopper.value = false
		let can_eh = sel_rows.length > 0;
		for (let i = 0; i < sel_rows.length; i++)
		{
			if (sel_rows[i].status === 'Accepted' || sel_rows[i].status === 'Deleted')
			{
				can_eh = false
				showDeletePopper.value = true
				break;
			}
		}
		return can_eh
	}
	return false
}

/**
 * True if there are rows selected and the cancel button should be enabled.
 * @type {ComputedRef<boolean>}
 */
const cancelEnabled = computed(()=>{
	return rowsSelected.value > 0
})

/**
 * Creates the text to show in the Information column
 * @param {InviteRecord} p_data Row data
 * @returns {string} Text to display in the column
 */
function getInfoText(p_data)
{
	if (p_data.error_eh)
	{
		return "Error Message: " + p_data.error_message;
	}
	else if (p_data.deleted_at)
	{
		return "Deleted by " + p_data.modifier_name;
	}
	else if (p_data.modified_by_user_id && p_data.modified_by_user_id > 0)
	{
		return "Invite resent by " + p_data.modifier_name
	}
	else if (p_data.invitee_user_id && p_data.invitee_user_id > 0)
	{
		return "Invited by " + p_data.inviter_name
	}
}
//#endregion Table Functions

</script>
